// ABOUTME: Enterprise-grade slide-out table editor with live preview.
// ABOUTME: Features professional data grid, cell editing, save button, and instant visual feedback.

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Check, AlertCircle, Loader2, Save } from 'lucide-react';
import { ReportTable } from './ReportTable';

interface TableContent {
  title: string;
  headers: string[];
  rows: (string | number)[][];
}

interface TableEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: TableContent;
  onSave: (data: TableContent) => void | Promise<void>;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function TableEditorModal({
  isOpen,
  onClose,
  initialData,
  onSave,
}: TableEditorModalProps) {
  const [data, setData] = useState<TableContent>(initialData);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Reset state when panel opens
  useEffect(() => {
    if (isOpen) {
      setData(initialData);
      setSaveStatus('idle');
      setSelectedCell(null);
      setHasUnsavedChanges(false);
    }
  }, [isOpen, initialData]);

  // Save function — supports both sync and async onSave via Promise.resolve()
  const handleSave = useCallback(async () => {
    setSaveStatus('saving');
    try {
      await Promise.resolve(onSave(data));
      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      // Close after brief delay to show "Saved" status
      setTimeout(() => onClose(), 500);
    } catch {
      setSaveStatus('error');
    }
  }, [data, onSave, onClose]);

  const updateTitle = useCallback((title: string) => {
    setData(prev => ({ ...prev, title }));
    setHasUnsavedChanges(true);
  }, []);

  const updateHeader = useCallback((index: number, value: string) => {
    setData(prev => ({
      ...prev,
      headers: prev.headers.map((h, i) => i === index ? value : h)
    }));
    setHasUnsavedChanges(true);
  }, []);

  const updateCell = useCallback((rowIndex: number, colIndex: number, value: string) => {
    setData(prev => ({
      ...prev,
      rows: prev.rows.map((row, ri) =>
        ri === rowIndex
          ? row.map((cell, ci) => ci === colIndex ? value : cell)
          : row
      )
    }));
    setHasUnsavedChanges(true);
  }, []);

  const addRow = useCallback(() => {
    const newRowIndex = data.rows.length;
    setData(prev => ({
      ...prev,
      rows: [...prev.rows, prev.headers.map(() => '')]
    }));
    setHasUnsavedChanges(true);
    setSelectedCell({ row: newRowIndex, col: 0 });
  }, [data.rows.length, data.headers]);

  const deleteRow = useCallback((index: number) => {
    setData(prev => ({
      ...prev,
      rows: prev.rows.filter((_, i) => i !== index)
    }));
    setHasUnsavedChanges(true);
    setSelectedCell(null);
  }, []);

  const addColumn = useCallback(() => {
    setData(prev => ({
      ...prev,
      headers: [...prev.headers, `Col ${prev.headers.length + 1}`],
      rows: prev.rows.map(row => [...row, ''])
    }));
    setHasUnsavedChanges(true);
  }, []);

  const deleteColumn = useCallback((index: number) => {
    setData(prev => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== index),
      rows: prev.rows.map(row => row.filter((_, i) => i !== index))
    }));
    setHasUnsavedChanges(true);
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Tab' && !e.shiftKey) {
      if (colIndex < data.headers.length - 1) {
        e.preventDefault();
        setSelectedCell({ row: rowIndex, col: colIndex + 1 });
      } else if (rowIndex < data.rows.length - 1) {
        e.preventDefault();
        setSelectedCell({ row: rowIndex + 1, col: 0 });
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      if (colIndex > 0) {
        e.preventDefault();
        setSelectedCell({ row: rowIndex, col: colIndex - 1 });
      } else if (rowIndex > 0) {
        e.preventDefault();
        setSelectedCell({ row: rowIndex - 1, col: data.headers.length - 1 });
      }
    } else if (e.key === 'ArrowDown' && rowIndex < data.rows.length - 1) {
      setSelectedCell({ row: rowIndex + 1, col: colIndex });
    } else if (e.key === 'ArrowUp' && rowIndex > 0) {
      setSelectedCell({ row: rowIndex - 1, col: colIndex });
    }
  }, [data.headers.length, data.rows.length, handleSave]);

  // Handle close with unsaved changes warning
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const confirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    onClose();
  }, [onClose]);

  const cancelDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
  }, []);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[9999] bg-black/40"
            onClick={handleClose}
          />

          {/* Slide-out Panel */}
          <motion.div
            ref={panelRef}
            initial={{ x: '100%', opacity: 0.8 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0.8 }}
            transition={{ type: 'spring', damping: 28, stiffness: 350 }}
            className="fixed right-0 top-0 bottom-0 z-[10000] w-[680px] max-w-[95vw] bg-surface-0 border-l border-surface-200 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-surface-200 bg-surface-50">
              <h2 className="text-base font-semibold text-surface-800">
                Edit Table Data
              </h2>
              <button
                onClick={handleClose}
                className="p-1.5 rounded-sm text-surface-500 hover:text-surface-800 hover:bg-surface-100 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Title Input */}
            <div className="px-5 py-3 border-b border-surface-200 bg-surface-50">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-surface-500 mb-2">
                Table Title
              </div>
              <input
                type="text"
                value={data.title}
                onChange={(e) => updateTitle(e.target.value)}
                placeholder="Enter table title..."
                className="w-full h-9 px-3 bg-surface-0 border border-surface-200 rounded-sm text-sm text-surface-800 placeholder-surface-400 focus:outline-none focus:border-primary-600 transition-colors"
              />
            </div>

            {/* Data Grid - Compact with max height */}
            <div className="flex-1 flex flex-col min-h-0 max-h-[280px] overflow-hidden">
              <div className="px-5 py-2 border-b border-surface-200 bg-surface-50 flex items-center justify-between shrink-0">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                  Data ({data.headers.length} cols × {data.rows.length} rows)
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={addColumn}
                    className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <Plus className="w-3 h-3" />
                    Column
                  </button>
                  <button
                    onClick={addRow}
                    className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <Plus className="w-3 h-3" />
                    Row
                  </button>
                </div>
              </div>

              {/* Compact Table */}
              <div className="flex-1 overflow-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-surface-0 z-10">
                    <tr className="border-b border-surface-200">
                      <th className="w-9 px-2 py-1.5 text-center text-[9px] font-semibold uppercase tracking-wider text-surface-500">#</th>
                      {data.headers.map((header, colIndex) => (
                        <th key={colIndex} className="min-w-[100px] px-1 py-1.5 relative group/header">
                          <input
                            type="text"
                            value={header}
                            onChange={(e) => updateHeader(colIndex, e.target.value)}
                            className="w-full h-6 px-2 bg-surface-50 border border-transparent rounded-sm text-[9px] font-semibold uppercase tracking-wider text-surface-500 focus:text-surface-800 focus:outline-none focus:border-primary-600/50 hover:border-surface-200 transition-colors"
                          />
                          {data.headers.length > 1 && (
                            <button
                              onClick={() => deleteColumn(colIndex)}
                              className="absolute right-0 top-1/2 -translate-y-1/2 p-0.5 rounded-sm text-surface-300 hover:text-error opacity-0 group-hover/header:opacity-100 transition-all cursor-pointer"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </th>
                      ))}
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="border-b border-surface-100 group/row"
                      >
                        <td className="px-2 py-1 text-center text-[11px] text-surface-500 font-mono">
                          {rowIndex + 1}
                        </td>
                        {row.map((cell, colIndex) => {
                          const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;

                          return (
                            <td
                              key={colIndex}
                              className={`px-1 py-1 ${isSelected ? 'bg-primary-50' : ''}`}
                            >
                              <input
                                type="text"
                                value={String(cell)}
                                onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
                                onFocus={() => setSelectedCell({ row: rowIndex, col: colIndex })}
                                onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                                className={`w-full h-7 px-2 bg-surface-50 border rounded-sm text-[13px] text-surface-800 focus:outline-none transition-colors ${
                                  isSelected
                                    ? 'border-primary-600/40 focus:border-primary-600'
                                    : 'border-transparent hover:border-surface-200 focus:border-primary-600/40'
                                }`}
                              />
                            </td>
                          );
                        })}
                        <td className="px-1 py-1">
                          <button
                            onClick={() => deleteRow(rowIndex)}
                            disabled={data.rows.length <= 1}
                            className="p-1 rounded-sm text-surface-300 hover:text-error hover:bg-error-light opacity-0 group-hover/row:opacity-100 disabled:opacity-20 disabled:cursor-not-allowed transition-all cursor-pointer"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Preview - Fills remaining vertical space */}
            <div className="flex-1 min-h-[200px] px-5 py-3 border-t border-surface-200 bg-surface-50 overflow-auto">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-surface-500 mb-2">
                Preview
              </div>
              <div className="rounded-sm border border-surface-200 overflow-hidden">
                <ReportTable
                  data={{ headers: data.headers, rows: data.rows }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 flex items-center justify-between border-t border-surface-200 bg-surface-50">
              <div className="flex items-center gap-3">
                {/* Save Status */}
                <AnimatePresence mode="wait">
                  {saveStatus === 'saving' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="flex items-center gap-1.5 text-xs text-surface-500"
                    >
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving</span>
                    </motion.div>
                  )}
                  {saveStatus === 'saved' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="flex items-center gap-1.5 text-xs text-success"
                    >
                      <Check className="w-4 h-4" />
                      <span>Saved</span>
                    </motion.div>
                  )}
                  {saveStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="flex items-center gap-1.5 text-xs text-error"
                    >
                      <AlertCircle className="w-4 h-4" />
                      <span>Save failed</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {hasUnsavedChanges && saveStatus === 'idle' && (
                  <span className="text-xs text-accent-600">
                    Unsaved changes
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleClose}
                  className="h-9 px-4 rounded-sm text-sm font-medium text-surface-600 hover:text-surface-800 hover:bg-surface-100 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saveStatus === 'saving'}
                  className={`h-9 px-5 rounded-sm text-sm font-medium flex items-center gap-2 transition-all cursor-pointer ${
                    hasUnsavedChanges
                      ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg shadow-primary-600/20'
                      : 'bg-surface-100 text-surface-800 hover:bg-surface-200'
                  }`}
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>

          {/* Discard Changes Confirmation */}
          <AnimatePresence>
            {showDiscardConfirm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[10001] flex items-center justify-center"
              >
                <div className="absolute inset-0 bg-black/60" onClick={cancelDiscard} />
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="relative bg-surface-0 border border-surface-200 rounded-sm shadow-modal p-5 max-w-sm mx-4"
                >
                  <h3 className="text-base font-semibold text-surface-800 mb-2">
                    Discard changes?
                  </h3>
                  <p className="text-sm text-surface-600 mb-5">
                    You have unsaved changes that will be lost if you close this panel.
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={cancelDiscard}
                      className="h-9 px-4 rounded-sm text-sm font-medium text-surface-600 hover:text-surface-800 hover:bg-surface-100 transition-colors cursor-pointer"
                    >
                      Keep Editing
                    </button>
                    <button
                      onClick={confirmDiscard}
                      className="h-9 px-4 rounded-sm text-sm font-medium bg-error text-white hover:bg-error/90 transition-colors cursor-pointer"
                    >
                      Discard
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
